import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import {
  Activity,
  BarChart2,
  Check,
  Download,
  ExternalLink,
  Eye,
  Map as MapIcon,
  MousePointerClick,
  Repeat2,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useDashReport } from '@/modules/dash/hooks/useDashReport'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { MapaDepartamentosPeru, PURPLE_SHADES, GREEN_SHADES } from '../components/MapaDepartamentosPeru'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

// ── Tipos ──────────────────────────────────────────────────────────────────

interface Performance {
  impressions: number
  clicks: number
  ctr: number
  viewability: number
  unique_impressions: number
  frequency: number
}

interface CiudadItem { region: string; impressions: number; clicks: number; ctr: number; mes: string }

interface MesData {
  totales:     { impressions: number; clicks: number; ctr: number }
  performance: Performance
  ciudad:      CiudadItem[]
  creativo:    unknown[]
  dispositivo: unknown[]
  diario:      unknown[]
}

interface ProgrammaticData {
  anual: { impressions: number; clicks: number; ctr: number }
  meses: Record<string, MesData>
}

// ── Constantes ─────────────────────────────────────────────────────────────

const PURPLE    = '#8d82bc'
const GREEN_600 = '#16a34a'

const BENCHMARK_CTR        = 0.20
const BENCHMARK_VIEWABILITY = 85

const MESES_2025 = ['octubre', 'noviembre', 'diciembre'] as const
const MESES_2026 = ['marzo', 'mayo', 'junio'] as const

const MES_LABEL_FULL: Record<string, string> = {
  octubre: 'Octubre', noviembre: 'Noviembre', diciembre: 'Diciembre',
  marzo: 'Marzo', mayo: 'Mayo', junio: 'Junio',
}

// ── Helpers ────────────────────────────────────────────────────────────────

function fmt(n: number)  { return n.toLocaleString('es-PE') }
function fmtK(n: number) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000)     return (n / 1_000).toFixed(1) + 'K'
  return n.toString()
}

function pctChange(prev: number, curr: number) {
  if (prev === 0) return null
  return ((curr - prev) / prev) * 100
}

function weightedAvg(meses: readonly string[], data: ProgrammaticData, key: keyof Performance) {
  let totalImp = 0, totalVal = 0
  for (const m of meses) {
    const p = data.meses[m]?.performance
    if (!p) continue
    totalImp += p.impressions
    totalVal += p.impressions * (p[key] as number)
  }
  return totalImp > 0 ? totalVal / totalImp : 0
}

// ── Subcomponentes ─────────────────────────────────────────────────────────

function KpiSkeleton() {
  return (
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Skeleton className="h-8 w-8 rounded-md" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-6 w-28" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

interface CompCardProps {
  label: string
  icon: React.ElementType
  val25: string
  val26: string
  pct: number | null
}

function CompCard({ label, icon: Icon, val25, val26, pct }: CompCardProps) {
  const up        = pct !== null && pct >= 0
  const Trend     = pct === null ? null : up ? TrendingUp : TrendingDown
  const pctColor  = pct === null ? '' : up ? GREEN_600 : '#d97706'
  const accentHex = pct === null ? PURPLE : up ? GREEN_600 : '#d97706'

  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-card"
      style={{ backgroundImage: `linear-gradient(to right, ${accentHex}18, transparent 60%)` }}
    >
      <div className="px-4 py-3 space-y-2">
        <div className="flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
        </div>
        <div className="grid grid-cols-3 items-center gap-1">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wide mb-0.5" style={{ color: PURPLE }}>2025</p>
            <p className="text-base font-bold text-foreground leading-tight">{val25}</p>
          </div>
          <div className="flex flex-col items-center">
            {Trend && pct !== null && (
              <>
                <div
                  className="flex items-center justify-center h-5 w-5 rounded-full text-white mb-0.5"
                  style={{ backgroundColor: pctColor }}
                >
                  {up
                    ? <Check className="h-3 w-3" strokeWidth={3} />
                    : <span className="text-[10px] font-bold leading-none">!</span>}
                </div>
                <div className="flex items-center gap-0.5" style={{ color: pctColor }}>
                  <Trend className="h-3 w-3 shrink-0" />
                  <span className="text-[11px] font-semibold">{pct > 0 ? '+' : ''}{pct.toFixed(1)}%</span>
                </div>
              </>
            )}
          </div>
          <div className="text-right">
            <p className="text-[10px] font-medium uppercase tracking-wide mb-0.5" style={{ color: GREEN_600 }}>2026</p>
            <p className="text-base font-bold text-foreground leading-tight">{val26}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Benchmark: 3 barras verticales [2025 | Bench | 2026]
interface BenchmarkTrioProps {
  label: string
  icon: React.ElementType
  val25: number
  val26: number
  benchmark: number
  decimals?: number
}

function BenchmarkTrio({ label, icon: Icon, val25, val26, benchmark, decimals = 1 }: BenchmarkTrioProps) {
  const max = Math.max(val25, val26, benchmark)

  function BarCol({ value, color, sublabel }: { value: number; color: string; sublabel: string }) {
    const h = max > 0 ? Math.round((value / max) * 120) : 0
    const above = value >= benchmark
    const C = above ? TrendingUp : TrendingDown
    const isBench = sublabel === 'Benchmark'
    return (
      <div className="flex flex-col items-center gap-1 w-16">
        <span className="text-xs font-bold" style={{ color }}>{value.toFixed(decimals)}%</span>
        <div className="w-10 rounded-md bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 relative" style={{ height: 120 }}>
          <div
            className="absolute bottom-0 left-0 right-0 transition-all"
            style={{ height: h, backgroundColor: color, borderRadius: h >= 120 ? 6 : '0 0 6px 6px' }}
          />
        </div>
        {isBench
          ? <span className="text-[10px] text-muted-foreground">—</span>
          : <div className="flex items-center gap-0.5" style={{ color }}>
              <C className="h-3 w-3" />
              <span className="text-[10px] font-semibold">{value >= benchmark ? '+' : ''}{(value - benchmark).toFixed(decimals)}%</span>
            </div>}
        <span className="text-[10px] text-muted-foreground font-medium">{sublabel}</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</span>
      </div>
      <div className="flex items-end gap-3">
        <BarCol value={val25} color={val25 >= benchmark ? GREEN_600 : '#d97706'} sublabel="2025" />
        <BarCol value={benchmark} color="#9ca3af" sublabel="Benchmark" />
        <BarCol value={val26} color={val26 >= benchmark ? GREEN_600 : '#d97706'} sublabel="2026" />
      </div>
    </div>
  )
}

// ── Página principal ────────────────────────────────────────────────────────

export function FerrerProgrammaticVsPage() {
  useDocumentTitle('Ferrer Programmatic | 2025 vs 2026')

  const { data: d25, isLoading: l25, error: e25 } = useDashReport<ProgrammaticData>('ferrer/programmatic/2025')
  const { data: d26, isLoading: l26, error: e26 } = useDashReport<ProgrammaticData>('ferrer/programmatic/2026')

  const isLoading = l25 || l26
  const error     = e25 || e26

  // ── Totales ───────────────────────────────────────────────────────────────

  const totals = useMemo(() => {
    if (!d25 || !d26) return null
    const imp25  = d25.anual.impressions
    const imp26  = d26.anual.impressions
    const clk25  = d25.anual.clicks
    const clk26  = d26.anual.clicks
    const ctr25  = d25.anual.ctr
    const ctr26  = d26.anual.ctr
    const vw25   = weightedAvg(MESES_2025, d25, 'viewability')
    const vw26   = weightedAvg(MESES_2026, d26, 'viewability')
    const fr25   = weightedAvg(MESES_2025, d25, 'frequency')
    const fr26   = weightedAvg(MESES_2026, d26, 'frequency')
    const uniq25 = MESES_2025.reduce((s, m) => s + (d25.meses[m]?.performance.unique_impressions ?? 0), 0)
    const uniq26 = MESES_2026.reduce((s, m) => s + (d26.meses[m]?.performance.unique_impressions ?? 0), 0)
    return { imp25, imp26, clk25, clk26, ctr25, ctr26, vw25, vw26, fr25, fr26, uniq25, uniq26 }
  }, [d25, d26])

  // ── Mapas de calor ───────────────────────────────────────────────────────

  const mapaData25 = useMemo(() => {
    if (!d25) return []
    const map = new Map<string, number>()
    for (const m of MESES_2025) {
      for (const c of d25.meses[m]?.ciudad ?? []) {
        map.set(c.region, (map.get(c.region) ?? 0) + c.impressions)
      }
    }
    return Array.from(map.entries()).map(([label, value]) => ({ label, value }))
  }, [d25])

  const mapaData26 = useMemo(() => {
    if (!d26) return []
    const map = new Map<string, number>()
    for (const m of MESES_2026) {
      for (const c of d26.meses[m]?.ciudad ?? []) {
        map.set(c.region, (map.get(c.region) ?? 0) + c.impressions)
      }
    }
    return Array.from(map.entries()).map(([label, value]) => ({ label, value }))
  }, [d26])

  // ── Datos gráficas ────────────────────────────────────────────────────────

  const allLabels = [
    ...MESES_2025.map(m => MES_LABEL_FULL[m]),
    ...MESES_2026.map(m => MES_LABEL_FULL[m]),
  ]

  const allColors = [...MESES_2025.map(() => PURPLE), ...MESES_2026.map(() => GREEN_600)]

  function makeChart(values: number[]) {
    return {
      labels: allLabels,
      datasets: [{
        label: 'Valor',
        data: values,
        backgroundColor: allColors,
        borderRadius: 4,
        barPercentage: 0.7,
        categoryPercentage: 0.85,
      }],
    }
  }

  const impChartData = useMemo(() => {
    if (!d25 || !d26) return null
    return makeChart([
      ...MESES_2025.map(m => d25.meses[m]?.performance.impressions ?? 0),
      ...MESES_2026.map(m => d26.meses[m]?.performance.impressions ?? 0),
    ])
  }, [d25, d26])

  const ctrChartData = useMemo(() => {
    if (!d25 || !d26) return null
    return makeChart([
      ...MESES_2025.map(m => d25.meses[m]?.performance.ctr ?? 0),
      ...MESES_2026.map(m => d26.meses[m]?.performance.ctr ?? 0),
    ])
  }, [d25, d26])

  const clkChartData = useMemo(() => {
    if (!d25 || !d26) return null
    return makeChart([
      ...MESES_2025.map(m => d25.meses[m]?.performance.clicks ?? 0),
      ...MESES_2026.map(m => d26.meses[m]?.performance.clicks ?? 0),
    ])
  }, [d25, d26])

  const noAxes = { grid: { display: false }, border: { display: false }, ticks: { display: false } }

  const barChartOpts = (fmtFn: (v: number) => string, tooltipFn: (v: number) => string) => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 28 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: { raw: unknown }) => ' ' + tooltipFn(ctx.raw as number),
        },
      },
      datalabels: {
        anchor: 'end' as const,
        align: 'top' as const,
        offset: 2,
        clip: false,
        color: '#6b7280',
        font: { size: 10 },
        display: (ctx: { dataset: { data: unknown[] }; dataIndex: number }) =>
          (ctx.dataset.data[ctx.dataIndex] as number) > 0,
        formatter: (v: number) => fmtFn(v),
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: '#6b7280', font: { size: 10 } },
      },
      y: { ...noAxes, beginAtZero: true },
    },
  })

  // ── Tabla mensual ─────────────────────────────────────────────────────────

  const tablaMeses = useMemo(() => {
    if (!d25 || !d26) return []
    const r25 = MESES_2025.map(m => {
      const p = d25.meses[m]?.performance
      return { mes: MES_LABEL_FULL[m] + ' 2025', year: 2025, impressions: p?.impressions ?? 0, clicks: p?.clicks ?? 0, ctr: p?.ctr ?? 0, viewability: p?.viewability ?? 0, frequency: p?.frequency ?? 0, unique: p?.unique_impressions ?? 0 }
    })
    const r26 = MESES_2026.map(m => {
      const p = d26.meses[m]?.performance
      return { mes: MES_LABEL_FULL[m] + ' 2026', year: 2026, impressions: p?.impressions ?? 0, clicks: p?.clicks ?? 0, ctr: p?.ctr ?? 0, viewability: p?.viewability ?? 0, frequency: p?.frequency ?? 0, unique: p?.unique_impressions ?? 0 }
    })
    return [...r25, ...r26]
  }, [d25, d26])

  // ── Loading / Error ───────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 space-y-6">
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-12 w-full rounded-xl" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <KpiSkeleton key={i} />)}
        </div>
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    )
  }

  if (error || !d25 || !d26 || !totals || !impChartData || !ctrChartData || !clkChartData) {
    return (
      <div className="p-6">
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardContent className="flex items-center justify-center h-40 text-sm text-muted-foreground">
            Error al cargar el reporte comparativo.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 space-y-6">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="py-4 px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-5">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-12 w-auto dark:invert" />
              <div className="border-l border-gray-200 dark:border-gray-700 pl-5">
                <h1 className="text-xl font-bold text-foreground leading-tight">
                  Reporte Programmatic 2025 vs 2026
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Comparativo trimestral de rendimiento Display · Q4 2025 vs Q2 2026
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <button
                onClick={() => window.print()}
                className="no-print inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <Download className="h-3.5 w-3.5" />
                Exportar PDF
              </button>
              <img
                src="/ferrer-programmatic/ferrer_logo.png"
                alt="Ferrer"
                className="h-12 w-auto"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Botones de acceso rápido ─────────────────────────────────────── */}
      <div className="no-print flex items-center gap-3">
        <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Ver reporte completo:</span>
        <Link
          to="/dash/ferrer/programmatic/2025"
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors hover:opacity-80"
          style={{ color: PURPLE, borderColor: PURPLE, background: `${PURPLE}18` }}
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Programmatic 2025
        </Link>
        <Link
          to="/dash/ferrer/programmatic/2026"
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors hover:opacity-80"
          style={{ color: GREEN_600, borderColor: GREEN_600, background: `${GREEN_600}18` }}
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Programmatic 2026
        </Link>
      </div>

      {/* ── KPIs comparativos ────────────────────────────────────────────── */}
      <div className="card-print-avoid grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <CompCard label="Impresiones"  icon={Eye}              val25={fmtK(totals.imp25)}             val26={fmtK(totals.imp26)}             pct={pctChange(totals.imp25,  totals.imp26)} />
        <CompCard label="Clicks"       icon={MousePointerClick} val25={fmt(totals.clk25)}              val26={fmt(totals.clk26)}              pct={pctChange(totals.clk25,  totals.clk26)} />
        <CompCard label="CTR"          icon={Activity}          val25={totals.ctr25.toFixed(2) + '%'}  val26={totals.ctr26.toFixed(2) + '%'}  pct={pctChange(totals.ctr25,  totals.ctr26)} />
        <CompCard label="Viewability"  icon={BarChart2}         val25={totals.vw25.toFixed(1) + '%'}   val26={totals.vw26.toFixed(1) + '%'}   pct={pctChange(totals.vw25,   totals.vw26)} />
        <CompCard label="Frecuencia"   icon={Repeat2}           val25={totals.fr25.toFixed(2)}         val26={totals.fr26.toFixed(2)}         pct={pctChange(totals.fr25,   totals.fr26)} />
        <CompCard label="Únicos"       icon={Users}             val25={fmtK(totals.uniq25)}            val26={fmtK(totals.uniq26)}            pct={pctChange(totals.uniq25, totals.uniq26)} />
      </div>

      {/* ── Benchmark + Mapas ───────────────────────────────────────────── */}
      <div className="card-print-avoid grid gap-4" style={{ gridTemplateColumns: '1fr 3fr 1fr' }}>
        {/* Mapa 2025 */}
        <MapaDepartamentosPeru
          title="Regiones · 2025"
          data={mapaData25}
          icon={MapIcon}
          formatValue={fmt}
          colorShades={PURPLE_SHADES}
        />

        {/* Benchmark + Resumen YoY */}
        <Card className="border border-gray-300 dark:border-gray-700 flex flex-col">
          <CardHeader className="space-y-0 pb-2">
            <div className="w-full text-center text-sm font-medium">Benchmark</div>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col gap-4 px-6">
            <div className="flex flex-row justify-around gap-8 py-2">
              <BenchmarkTrio
                label="CTR"
                icon={Activity}
                val25={totals.ctr25}
                val26={totals.ctr26}
                benchmark={BENCHMARK_CTR}
                decimals={2}
              />
              <BenchmarkTrio
                label="Viewability"
                icon={Eye}
                val25={totals.vw25}
                val26={totals.vw26}
                benchmark={BENCHMARK_VIEWABILITY}
              />
            </div>

            {/* Resumen cambios YoY */}
            <div className="border-t border-gray-100 dark:border-gray-800 pt-4 flex-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide text-center mb-3">
                Variación Q4 2025 → Q2 2026
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {[
                  { label: 'Impresiones',  pct: pctChange(totals.imp25,  totals.imp26),  icon: Eye },
                  { label: 'Viewability',  pct: pctChange(totals.vw25,   totals.vw26),   icon: Eye },
                  { label: 'Clicks',       pct: pctChange(totals.clk25,  totals.clk26),  icon: MousePointerClick },
                  { label: 'Frecuencia',   pct: pctChange(totals.fr25,   totals.fr26),   icon: Repeat2 },
                  { label: 'CTR',          pct: pctChange(totals.ctr25,  totals.ctr26),  icon: Activity },
                  { label: 'Únicos',       pct: pctChange(totals.uniq25, totals.uniq26), icon: Users },
                ].map(({ label, pct, icon: Icon }) => {
                  const up = pct !== null && pct >= 0
                  const color = pct === null ? '#9ca3af' : up ? GREEN_600 : '#d97706'
                  const Trend = pct === null ? null : up ? TrendingUp : TrendingDown
                  return (
                    <div key={label} className="flex items-center justify-between gap-1 rounded-md bg-gray-50 dark:bg-gray-800/40 px-2.5 py-1.5">
                      <div className="flex items-center gap-1 min-w-0">
                        <Icon className="h-3 w-3 text-muted-foreground shrink-0" />
                        <span className="text-[11px] text-muted-foreground truncate">{label}</span>
                      </div>
                      {pct !== null && Trend && (
                        <div className="flex items-center gap-0.5 shrink-0 font-bold text-[11px]" style={{ color }}>
                          <Trend className="h-3 w-3" />
                          {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mapa 2026 */}
        <MapaDepartamentosPeru
          title="Regiones · 2026"
          data={mapaData26}
          icon={MapIcon}
          formatValue={fmt}
          colorShades={GREEN_SHADES}
        />
      </div>

      {/* ── Gráficas mensuales ───────────────────────────────────────────── */}
      <div className="card-print-avoid grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Impresiones por mes */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader className="space-y-0 pb-0">
            <div className="flex items-center justify-center gap-2">
              <Eye className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">Impresiones por mes</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-4 pt-2">
            {/* Corchetes 2025 / 2026 */}
            <div className="flex gap-2 mb-0" style={{ paddingLeft: 0, paddingRight: 0 }}>
              <div style={{ flex: 3 }} className="flex flex-col items-center">
                <span className="text-[11px] font-semibold leading-none mb-1" style={{ color: PURPLE }}>2025 · Q4</span>
                <div style={{ width: '100%', height: 8, borderTop: `2px solid ${PURPLE}`, borderLeft: `2px solid ${PURPLE}`, borderRight: `2px solid ${PURPLE}`, borderRadius: '3px 3px 0 0', opacity: 0.7 }} />
              </div>
              <div style={{ flex: 3 }} className="flex flex-col items-center">
                <span className="text-[11px] font-semibold leading-none mb-1" style={{ color: GREEN_600 }}>2026 · Q2</span>
                <div style={{ width: '100%', height: 8, borderTop: `2px solid ${GREEN_600}`, borderLeft: `2px solid ${GREEN_600}`, borderRight: `2px solid ${GREEN_600}`, borderRadius: '3px 3px 0 0', opacity: 0.7 }} />
              </div>
            </div>
            <div style={{ height: 240 }}>
              <Bar
                data={impChartData}
                options={barChartOpts(fmtK, fmt)}
                plugins={[ChartDataLabels]}
              />
            </div>
          </CardContent>
        </Card>

        {/* CTR por mes */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader className="space-y-0 pb-0">
            <div className="flex items-center justify-center gap-2">
              <Activity className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">CTR por mes (%)</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-4 pt-2">
            <div className="flex gap-2 mb-0">
              <div style={{ flex: 3 }} className="flex flex-col items-center">
                <span className="text-[11px] font-semibold leading-none mb-1" style={{ color: PURPLE }}>2025 · Q4</span>
                <div style={{ width: '100%', height: 8, borderTop: `2px solid ${PURPLE}`, borderLeft: `2px solid ${PURPLE}`, borderRight: `2px solid ${PURPLE}`, borderRadius: '3px 3px 0 0', opacity: 0.7 }} />
              </div>
              <div style={{ flex: 3 }} className="flex flex-col items-center">
                <span className="text-[11px] font-semibold leading-none mb-1" style={{ color: GREEN_600 }}>2026 · Q2</span>
                <div style={{ width: '100%', height: 8, borderTop: `2px solid ${GREEN_600}`, borderLeft: `2px solid ${GREEN_600}`, borderRight: `2px solid ${GREEN_600}`, borderRadius: '3px 3px 0 0', opacity: 0.7 }} />
              </div>
            </div>
            <div style={{ height: 240 }}>
              <Bar
                data={ctrChartData}
                options={barChartOpts((v) => v.toFixed(2) + '%', (v) => v.toFixed(2) + '%')}
                plugins={[ChartDataLabels]}
              />
            </div>
          </CardContent>
        </Card>

        {/* Clicks por mes */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader className="space-y-0 pb-0">
            <div className="flex items-center justify-center gap-2">
              <MousePointerClick className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">Clicks por mes</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-4 pt-2">
            <div className="flex gap-2 mb-0">
              <div style={{ flex: 3 }} className="flex flex-col items-center">
                <span className="text-[11px] font-semibold leading-none mb-1" style={{ color: PURPLE }}>2025 · Q4</span>
                <div style={{ width: '100%', height: 8, borderTop: `2px solid ${PURPLE}`, borderLeft: `2px solid ${PURPLE}`, borderRight: `2px solid ${PURPLE}`, borderRadius: '3px 3px 0 0', opacity: 0.7 }} />
              </div>
              <div style={{ flex: 3 }} className="flex flex-col items-center">
                <span className="text-[11px] font-semibold leading-none mb-1" style={{ color: GREEN_600 }}>2026 · Q2</span>
                <div style={{ width: '100%', height: 8, borderTop: `2px solid ${GREEN_600}`, borderLeft: `2px solid ${GREEN_600}`, borderRight: `2px solid ${GREEN_600}`, borderRadius: '3px 3px 0 0', opacity: 0.7 }} />
              </div>
            </div>
            <div style={{ height: 240 }}>
              <Bar
                data={clkChartData}
                options={barChartOpts(fmtK, fmt)}
                plugins={[ChartDataLabels]}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Tabla mensual ────────────────────────────────────────────────── */}
      <Card className="card-print-avoid border border-gray-300 dark:border-gray-700">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-2">
            <BarChart2 className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Detalle mensual</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-800 text-sm">
              <thead className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground">Mes</th>
                  <th className="px-4 py-3 text-right font-semibold text-muted-foreground">
                    <div className="flex items-center justify-end gap-1.5"><Eye className="h-3.5 w-3.5" />Impresiones</div>
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-muted-foreground">
                    <div className="flex items-center justify-end gap-1.5"><MousePointerClick className="h-3.5 w-3.5" />Clicks</div>
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-muted-foreground">
                    <div className="flex items-center justify-end gap-1.5"><Activity className="h-3.5 w-3.5" />CTR</div>
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-muted-foreground">
                    <div className="flex items-center justify-end gap-1.5"><Eye className="h-3.5 w-3.5" />Viewability</div>
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-muted-foreground">
                    <div className="flex items-center justify-end gap-1.5"><Repeat2 className="h-3.5 w-3.5" />Frecuencia</div>
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-muted-foreground">
                    <div className="flex items-center justify-end gap-1.5"><Users className="h-3.5 w-3.5" />Únicos</div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                {tablaMeses.map((row, i) => {
                  const color    = row.year === 2025 ? PURPLE : GREEN_600
                  const ctrAbove = row.ctr >= BENCHMARK_CTR
                  const vwAbove  = row.viewability >= BENCHMARK_VIEWABILITY
                  return (
                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                      <td className="px-4 py-2.5 font-semibold" style={{ color }}>{row.mes}</td>
                      <td className="px-4 py-2.5 text-right text-foreground">{fmt(row.impressions)}</td>
                      <td className="px-4 py-2.5 text-right text-foreground">{fmt(row.clicks)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1" style={{ color: ctrAbove ? GREEN_600 : '#d97706' }}>
                          {ctrAbove ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                          <span className="font-medium">{row.ctr.toFixed(2)}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1" style={{ color: vwAbove ? GREEN_600 : '#d97706' }}>
                          {vwAbove ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                          <span className="font-medium">{row.viewability.toFixed(1)}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right text-foreground">{row.frequency.toFixed(2)}</td>
                      <td className="px-4 py-2.5 text-right text-foreground">{fmt(row.unique)}</td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot className="border-t-2 border-gray-200 dark:border-gray-700">
                {[
                  { label: 'Total 2025', year: 2025, imp: totals.imp25, clk: totals.clk25, ctr: totals.ctr25, vw: totals.vw25, fr: totals.fr25, uniq: totals.uniq25 },
                  { label: 'Total 2026', year: 2026, imp: totals.imp26, clk: totals.clk26, ctr: totals.ctr26, vw: totals.vw26, fr: totals.fr26, uniq: totals.uniq26 },
                ].map((t) => (
                  <tr key={t.year} className="bg-gray-50 dark:bg-gray-900">
                    <td className="px-4 py-2.5 font-bold text-xs uppercase" style={{ color: t.year === 2025 ? PURPLE : GREEN_600 }}>{t.label}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-gray-900 dark:text-gray-100">{fmt(t.imp)}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-gray-900 dark:text-gray-100">{fmt(t.clk)}</td>
                    <td className="px-4 py-2.5 text-right font-bold">
                      <div className="flex items-center justify-end gap-1" style={{ color: t.ctr >= BENCHMARK_CTR ? GREEN_600 : '#d97706' }}>
                        {t.ctr >= BENCHMARK_CTR ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                        {t.ctr.toFixed(2)}%
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right font-bold">
                      <div className="flex items-center justify-end gap-1" style={{ color: t.vw >= BENCHMARK_VIEWABILITY ? GREEN_600 : '#d97706' }}>
                        {t.vw >= BENCHMARK_VIEWABILITY ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                        {t.vw.toFixed(1)}%
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right font-bold text-gray-900 dark:text-gray-100">{t.fr.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-gray-900 dark:text-gray-100">{fmt(t.uniq)}</td>
                  </tr>
                ))}
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

    </div>
  )
}
